/**
 * client/src/pages/billing.tsx
 *
 * Subscription management page — /billing (protected route).
 *
 * Sections:
 *  1. Current Subscription Status card
 *  2. Plan selector grid (Subscribe / Upgrade / Downgrade)
 *  3. Cancel confirmation dialog
 */

import React, { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getVisiblePlans, Plan } from "@shared/plans";
import {
  useSubscriptionStatus,
  useCancelSubscription,
} from "@/hooks/useBillingSubscription";
import { useRazorpayCheckout } from "@/hooks/useRazorpayCheckout";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { useGenerationLimit } from "@/hooks/useGenerationLimit";
import {
  Star,
  Zap,
  Rocket,
  Check,
  Loader2,
  AlertTriangle,
  Calendar,
  CreditCard,
  ArrowUpCircle,
  ArrowDownCircle,
  Activity,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatPrice = (paise: number) => `₹${Math.round(paise / 100)}`;

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const PLAN_ICONS: Record<string, React.ReactNode> = {
  FREE: <Star className="h-6 w-6 text-slate-500" />,
  PRO: <Zap className="h-6 w-6 text-violet-500" />,
  ENTERPRISE: <Rocket className="h-6 w-6 text-orange-500" />,
};

const PLAN_RANK: Record<string, number> = { FREE: 0, PRO: 1, ENTERPRISE: 2 };

const STATUS_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active:              { label: "Active",             variant: "default" },
  cancelled:           { label: "Cancelled",          variant: "destructive" },
  pending_cancellation:{ label: "Cancels at period end", variant: "secondary" },
  halted:              { label: "Payment failed",     variant: "destructive" },
  completed:           { label: "Expired",            variant: "outline" },
};

// ── DailyUsageCard ────────────────────────────────────────────────────────────

function DailyUsageCard() {
  const { data, isLoading } = useGenerationLimit();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading usage…
        </CardContent>
      </Card>
    );
  }

  const isUnlimited = data?.limit === null;
  const used = data?.used ?? 0;
  const limit = data?.limit ?? 5;
  const remaining = data?.remaining ?? 0;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const isNearLimit = !isUnlimited && pct >= 80;
  const isAtLimit = !isUnlimited && used >= limit;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base font-semibold">Today's AI Generation Usage</CardTitle>
        </div>
        <CardDescription>Resets at midnight UTC</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isUnlimited ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-green-500" />
            <span>Unlimited generations — no daily cap on your plan.</span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className={isAtLimit ? "text-destructive font-medium" : "text-foreground"}>
                {used} of {limit} generations used today
              </span>
              <span className={isAtLimit ? "text-destructive" : "text-muted-foreground"}>
                {isAtLimit ? "Limit reached" : `${remaining} remaining`}
              </span>
            </div>
            <Progress
              value={pct}
              className={cn(
                "h-2",
                isAtLimit
                  ? "[&>div]:bg-destructive"
                  : isNearLimit
                  ? "[&>div]:bg-amber-500"
                  : ""
              )}
            />
            {isAtLimit && (
              <p className="text-xs text-destructive">
                You've reached your daily limit. Upgrade to Pro or Enterprise for unlimited AI generations.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── SubscriptionStatusCard ────────────────────────────────────────────────────

interface StatusCardProps {
  onCancel: () => void;
}

function SubscriptionStatusCard({ onCancel }: StatusCardProps) {
  const { data: status, isLoading } = useSubscriptionStatus();
  const { user } = useAuth();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading subscription…
        </CardContent>
      </Card>
    );
  }

  const planCode = status?.planCode ?? user?.planCode ?? "FREE";
  const hasPaidSub = status?.hasSubscription && planCode !== "FREE";
  const badge = status?.status ? STATUS_BADGE[status.status] : null;

  return (
    <Card className="border-2">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {PLAN_ICONS[planCode] ?? <Star className="h-6 w-6" />}
            <div>
              <CardTitle className="text-xl">
                {planCode === "FREE" ? "Free Plan" : planCode === "PRO" ? "Pro Author" : "Enterprise"}
              </CardTitle>
              <CardDescription>Current subscription</CardDescription>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {badge && (
              <Badge variant={badge.variant}>{badge.label}</Badge>
            )}
            {hasPaidSub &&
              status?.status !== "cancelled" &&
              status?.status !== "pending_cancellation" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={onCancel}
                >
                  Cancel subscription
                </Button>
              )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-wrap gap-6 text-sm text-muted-foreground">
        {status?.renewsAt && (
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4" />
            <span>
              {status.status === "pending_cancellation" ? "Expires" : "Renews"}{" "}
              {formatDate(status.renewsAt)}
            </span>
          </div>
        )}
        {status?.razorpayDetails?.id && (
          <div className="flex items-center gap-1.5">
            <CreditCard className="h-4 w-4" />
            <span className="font-mono text-xs">{status.razorpayDetails.id}</span>
          </div>
        )}
        {status?.status === "halted" && (
          <div className="flex items-center gap-1.5 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            <span>Payment failed — please update your payment method in Razorpay</span>
          </div>
        )}
        {!hasPaidSub && planCode === "FREE" && (
          <span>Upgrade to Pro or Enterprise for unlimited AI writing.</span>
        )}
      </CardContent>
    </Card>
  );
}

// ── BillingPlanCard ───────────────────────────────────────────────────────────

interface BillingPlanCardProps {
  plan: Plan;
  currentPlanCode: string;
  subscriptionStatus: string | null | undefined;
  onSubscribe: (planId: "pro" | "enterprise") => void;
  isLoading: boolean;
  loadingPlanId: string | null;
}

function BillingPlanCard({
  plan,
  currentPlanCode,
  subscriptionStatus,
  onSubscribe,
  isLoading,
  loadingPlanId,
}: BillingPlanCardProps) {
  const isCurrent = currentPlanCode === plan.code;
  const isFree = plan.code === "FREE";
  const planId = plan.code.toLowerCase() as "pro" | "enterprise";

  const currentRank = PLAN_RANK[currentPlanCode] ?? 0;
  const thisRank = PLAN_RANK[plan.code] ?? 0;
  const isUpgrade = thisRank > currentRank;
  const isDowngrade = thisRank < currentRank;

  const isPendingCancel =
    isCurrent &&
    (subscriptionStatus === "cancelled" || subscriptionStatus === "pending_cancellation");

  const getButtonLabel = () => {
    if (isFree) return "Free — no action needed";
    if (isCurrent && !isPendingCancel) return "Current plan";
    if (isPendingCancel) return `Resubscribe — ₹${Math.round((plan.monthlyPricePaise ?? 0) / 100)}/mo`;
    if (isUpgrade) return (
      <span className="flex items-center gap-1.5">
        <ArrowUpCircle className="h-4 w-4" /> Upgrade — {formatPrice(plan.monthlyPricePaise ?? 0)}/mo
      </span>
    );
    if (isDowngrade) return (
      <span className="flex items-center gap-1.5">
        <ArrowDownCircle className="h-4 w-4" /> Switch — {formatPrice(plan.monthlyPricePaise ?? 0)}/mo
      </span>
    );
    // New subscriber (from FREE or expired)
    return `Subscribe — ${formatPrice(plan.monthlyPricePaise ?? 0)}/mo`;
  };

  const isButtonDisabled = isFree || (isCurrent && !isPendingCancel);
  const thisLoading = isLoading && loadingPlanId === planId;

  return (
    <Card
      className={`relative flex flex-col ${
        plan.isPopular ? "border-primary ring-1 ring-primary" : ""
      } ${isCurrent ? "bg-muted/30" : ""}`}
    >
      {plan.isPopular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
          Most Popular
        </Badge>
      )}

      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          {PLAN_ICONS[plan.code] ?? <Star className="h-6 w-6" />}
          <div>
            <CardTitle>{plan.name}</CardTitle>
            {isCurrent && !isPendingCancel && (
              <Badge variant="secondary" className="mt-1 text-xs">
                Your plan
              </Badge>
            )}
          </div>
        </div>
        <CardDescription className="mt-1">{plan.description}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 flex-1">
        {/* Price display */}
        <div>
          {isFree ? (
            <div className="text-3xl font-bold">Free</div>
          ) : (
            <div>
              <span className="text-3xl font-bold">
                {formatPrice(plan.monthlyPricePaise ?? 0)}
              </span>
              <span className="text-muted-foreground">/mo</span>
              {plan.annualPricePaise && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  or {formatPrice(plan.annualPricePaise)}/yr · save {plan.annualDiscountPercent}%
                </p>
              )}
            </div>
          )}
        </div>

        {/* Feature list */}
        <ul className="space-y-1.5 flex-1">
          {plan.dailyGenerationLimit !== null ? (
            <li className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-green-500" />
              {plan.dailyGenerationLimit} AI generations/day
            </li>
          ) : (
            <li className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-green-500" />
              Unlimited AI generations
            </li>
          )}
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 shrink-0 text-green-500" />
              {f}
            </li>
          ))}
        </ul>

        {/* CTA button */}
        <Button
          className="w-full mt-auto"
          variant={plan.isPopular || plan.code === "ENTERPRISE" ? "default" : "outline"}
          disabled={isButtonDisabled || isLoading}
          onClick={() => !isButtonDisabled && onSubscribe(planId)}
        >
          {thisLoading ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Processing…
            </span>
          ) : (
            getButtonLabel()
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── BillingPage ───────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const plans = getVisiblePlans();

  const { data: subStatus } = useSubscriptionStatus();
  const { initiateCheckout, loading: checkoutLoading } = useRazorpayCheckout();
  const cancelMutation = useCancelSubscription();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  // Redirect unauthenticated users
  if (!user) {
    navigate("/auth?next=/billing");
    return null;
  }

  const currentPlanCode = subStatus?.planCode ?? user.planCode ?? "FREE";

  const handleSubscribe = async (planId: "pro" | "enterprise") => {
    setLoadingPlanId(planId);
    try {
      await initiateCheckout(planId);
    } finally {
      setLoadingPlanId(null);
    }
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);
    cancelMutation.mutate();
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto max-w-5xl py-10 px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── Page header ── */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Billing & Subscription</h1>
          <p className="mt-1 text-muted-foreground">
            Manage your plan, upgrade, or cancel anytime.
          </p>
        </div>

        {/* ── Daily usage progress ── */}
        <DailyUsageCard />

        {/* ── Current subscription status ── */}
        <SubscriptionStatusCard onCancel={() => setCancelDialogOpen(true)} />

        {/* ── Plan selector ── */}
        <section>
          <h2 className="text-xl font-semibold mb-1">Available Plans</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Pay securely via the Razorpay checkout popup — no redirect needed.
            Your plan activates immediately after payment.
          </p>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <BillingPlanCard
                key={plan.code}
                plan={plan}
                currentPlanCode={currentPlanCode}
                subscriptionStatus={subStatus?.status}
                onSubscribe={handleSubscribe}
                isLoading={checkoutLoading}
                loadingPlanId={loadingPlanId}
              />
            ))}
          </div>
        </section>

        {/* ── Billing history link ── */}
        <section className="flex items-center justify-between py-4 border-t">
          <div>
            <p className="font-medium">Payment history</p>
            <p className="text-sm text-muted-foreground">
              View past invoices and payments
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate("/settings")}>
            View in Settings
          </Button>
        </section>

        {/* ── Cancel confirmation dialog ── */}
        <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
              <AlertDialogDescription>
                Your{" "}
                <strong>
                  {currentPlanCode === "PRO" ? "Pro Author" : "Enterprise"}
                </strong>{" "}
                plan stays active until the end of the current billing period.
                After that, your account reverts to the Free tier (5 AI
                generations/day, Gemini Flash only).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep my plan</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleConfirmCancel}
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cancelling…
                  </span>
                ) : (
                  "Yes, cancel subscription"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
